import setCssVar from "../utils/setCssVar";
import throttle from "../utils/throttle";

export default class AnchorLinks {
  constructor(elem, _) {
    this.elem = elem;
    this.list = this.elem.querySelector("[data-anchor-list]");
    this.title = this.elem.querySelector("[data-anchor-title]");
    this.selectedLabel = this.elem.querySelector("[data-selected-label]");
    this.componentRoot = document.querySelector("[data-components]");
    this.toggle = this.elem.querySelector("[data-anchor-toggle]");
    this.links = [];
    this.headings = [];
    this.activeLink = null;
    this.isExpanded = false;
    this.scrollOffset = 250; // Adjust based on your header height and desired offset
    this.userScrolling = false; // Track if user is actively scrolling
    this.scrollTimer = null; // Timer for scroll events
    this.boundHandleTitleClick = null; // Store bound function reference

    this.deviceType = null;
    this.previousDeviceType = null;


    // Constructor initialization complete
  }

  init() {
    if (!this.componentRoot || !this.list) {
      return;
    }

    this.headings = this.componentRoot.querySelectorAll("h2[id]");

    if (this.headings.length === 0) {
      return;
    }

    // Set up initial styling and accessibility for the title
    if (this.title) {
      this.title.setAttribute('role', 'button');
      this.title.setAttribute('aria-expanded', 'false');
      this.title.setAttribute('tabindex', '0');

      // Don't show divider line initially (only when expanded or link selected)
      this.title.classList.remove('anchor-links__title--show-divider');
    }

    this.createLinks();
    this.setupEventListeners();

    // Set up IntersectionObserver for each heading
    this.setupIntersectionObserver();

    // Initial toggle visibility check - should happen after active link is set
    if (this.toggle) {
      this.checkToggleVisibility();
    }
    // Initialization complete
    setCssVar(this.componentRoot, '--anchor-links-height', `${this.elem.offsetHeight}px`);
    // window.addEventListener('resize', this.onResize.bind(this));

    this.deviceEventController();
  }

  createLinks() {
    this.headings.forEach((heading) => {
      const id = heading.id;
      const text = heading.textContent;

      const li = document.createElement("li");
      li.classList.add("anchor-links__item");

      const a = document.createElement("a");
      a.classList.add("anchor-links__link");

      const span = document.createElement("span");
      span.classList.add("anchor-links__text");
      span.textContent = text;

      a.href = `#${id}`;
      a.dataset.target = id;
      a.appendChild(span);

      li.appendChild(a);
      this.list.appendChild(li);

      this.links.push(a);
    });
  }

  setupEventListeners() {
    // Add click event listeners to each anchor link
    this.links.forEach(link => {
      link.addEventListener('click', this.handleLinkClick.bind(this));
    });

    // Add toggle functionality if toggle element exists
    if (this.toggle) {
      this.toggle.addEventListener('click', this.handleToggleClick.bind(this));
      // Check if we need to show the toggle based on list height
      this.checkToggleVisibility();
    }

    // Add a more direct way to detect clicks on the title area
    // This uses event delegation at the component level
    this.elem.addEventListener('click', (e) => {
      // Check if the click was within the title or its children
      if (this.title && (e.target === this.title || this.title.contains(e.target))) {
        // Handle title clicks
        this.handleTitleClick(e);
      }
    });

    // Add click event to title
    if (this.title) {
      // Store the bound function so we can remove it later if needed
      this.boundHandleTitleClick = this.handleTitleClick.bind(this);
      // Remove any existing click handlers first to avoid duplicates
      this.title.removeEventListener('click', this.boundHandleTitleClick);
      // Add click event to the title element
      this.title.addEventListener('click', this.boundHandleTitleClick);
      // Also add keyboard accessibility
      this.title.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleTitleClick(e);
        }
      });
      // Add visual cue for clickability - caret icon (will be shown/hidden via CSS)
      // Add the dropdown indicator as a separate element
      const indicator = document.createElement('span');
      indicator.className = 'dropdown-indicator';
      // Empty indicator element - the icon will be added via CSS
      this.title.appendChild(indicator);
    }
  }

  // Set up IntersectionObserver for each heading
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // eslint-disable-next-line no-console
      console.warn('IntersectionObserver API is not supported.');
      return;
    }
    const observerOptions = {
      root: null,
      rootMargin: '-25% 0px -25% 0px',
      threshold: 0
    };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const activeLink = this.links.find(link => link.dataset.target === id);
          if (activeLink) {
            this.links.forEach(link => link.classList.remove('anchor-links__link--active'));
            activeLink.classList.add('anchor-links__link--active');
          }
        }
      });
    }, observerOptions);
    this.headings.forEach(heading => {
      this.observer.observe(heading);
    });
  }


  checkActiveSection() {
    if (this.headings.length === 0) return;
    const scrollPos = window.scrollY + this.scrollOffset + 1; // +1 to favor next section as soon as it enters
    let currentSection = null;
    for (let i = this.headings.length - 1; i >= 0; i--) {
      const heading = this.headings[i];
      const headingTop = heading.getBoundingClientRect().top + window.scrollY;
      if (scrollPos >= headingTop) {
        currentSection = heading.id;
        break;
      }
    }
    if (!currentSection && this.headings.length > 0) {
      currentSection = this.headings[0].id;
    }
    if (currentSection) {
      const activeLink = this.links.find(link => link.dataset.target === currentSection);
      if (activeLink) {
        // Remove active class from all links
        this.links.forEach(link => link.classList.remove('anchor-links__link--active'));
        // Add active class to the current link
        activeLink.classList.add('anchor-links__link--active');
        // Scroll the menu so the active link is at the top
        const activeItem = activeLink.parentElement;
        if (activeItem && !this.isExpanded) {
          const listRect = this.list.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();
          // Only scroll if not already at/near top
          if (itemRect.top < listRect.top + 5 || itemRect.bottom > listRect.bottom) {
            this.list.scrollTop += (activeItem.offsetTop - 10) - this.list.scrollTop;
          }
        }
      }
    }
  }


  handleTitleClick(e) {
    // Stop event propagation and prevent default to avoid any unexpected behavior
    e.preventDefault();
    e.stopPropagation();

    // Toggle expanded state
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.list.classList.add('anchor-links__list--expanded-mobile');
      this.title.classList.add('anchor-links__title--expanded-mobile');

      // Add class to show divider line
      // this.title.classList.add('anchor-links__title--show-divider');

      // Update ARIA attributes
      this.title.setAttribute('aria-expanded', 'true');
    } else {
      this.list.classList.remove('anchor-links__list--expanded-mobile');
      this.title.classList.remove('anchor-links__title--expanded-mobile');

      // Only remove divider class if no selected label text
      if (!this.selectedLabel || !this.selectedLabel.textContent.trim()) {
        // this.title.classList.remove('anchor-links__title--show-divider');
      }

      // Update ARIA attributes
      this.title.setAttribute('aria-expanded', 'false');
    }

    // Force a redraw/reflow to ensure the browser applies the changes
    void this.list.offsetHeight;
  }

  checkToggleVisibility() {
    // Get the actual height of the list
    const listHeight = this.list.scrollHeight;
    const maxHeight = 200; // Same as the CSS variable

    // Only show toggle if the list is taller than max-height
    if (listHeight > maxHeight) {
      // this.toggle.style.display = 'block';
    } else {
      this.toggle.style.display = 'none';
    }
  };

  handleToggleClick() {
    this.isExpanded = !this.isExpanded;

    // Toggle the expanded class on the list
    this.list.classList.toggle('anchor-links__list--expanded', this.isExpanded);

    if (this.isExpanded) {
      // When expanding
      this.toggle.classList.add('anchor-links__toggle--expanded');
      this.toggle.textContent = 'Show Less';

      // Reset scroll position when expanded for better UX
      this.list.scrollTop = 0;
    } else {
      // When collapsing
      this.toggle.classList.remove('anchor-links__toggle--expanded');
      this.toggle.textContent = 'Show More';

      // Force immediate reflow to ensure the collapse transition starts
      void this.list.offsetHeight;

      // Final attempt - after transition should be complete
      setTimeout(() => this.scrollActiveIntoView(), 300);
    }

    // update css var
    setCssVar(this.componentRoot, '--anchor-links-height', `${this.elem.offsetHeight}px`);
  };

  handleLinkClick(e) {
    // Prevent default behavior and handle scrolling manually on all devices
    e.preventDefault();

    // Update active state immediately on click
    const clickedLink = e.currentTarget;

    // Remove active class from all links
    this.links.forEach(link => link.classList.remove('anchor-links__link--active'));
    // Add active class to clicked link
    clickedLink.classList.add('anchor-links__link--active');

    // Optionally, update the selected label text for dropdown mode (mobile and tablet)
    if (this.selectedLabel) {
      const linkText = clickedLink.querySelector('.anchor-links__text');
      if (linkText) {
        this.selectedLabel.textContent = linkText.textContent;
        if (this.title && linkText.textContent.trim()) {
          this.title.classList.add('anchor-links__title--show-divider');
        }
      }
    }

    // Manually scroll to the target section
    const targetId = clickedLink.getAttribute('href').substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const scrollPos = window.scrollY + rect.top - this.scrollOffset;
      window.scrollTo({
        top: scrollPos,
        behavior: 'smooth'
      });
    }
  }

  // checkActiveSection removed: no longer needed for click-only highlighting

  setActiveLink(activeLink) {
    // Remove active class from previous active link
    if (this.activeLink) {
      this.activeLink.classList.remove('anchor-links__link--active');
    }

    // Add active class to new active link
    activeLink.classList.add('anchor-links__link--active');
    this.activeLink = activeLink;

    // Update the selected label text for dropdown mode (mobile and tablet)
    if (this.selectedLabel) {
      const linkText = activeLink.querySelector('.anchor-links__text');
      if (linkText) {
        this.selectedLabel.textContent = linkText.textContent;

        // If there's now selected label text, add the divider class (even when dropdown is closed)
        if (this.title && linkText.textContent.trim()) {
          this.title.classList.add('anchor-links__title--show-divider');
        }
      }
    }

    // If list is currently collapsed, make sure active link is visible
    if (!this.isExpanded) {
      this.scrollActiveIntoView();
    }

    // Close the dropdown after selection if it's expanded
    if (this.isExpanded) {
      // Add small delay to ensure the user sees what they clicked
      // setTimeout(() => {
      //   this.isExpanded = false;
      //   this.list.classList.remove('anchor-links__list--expanded-mobile');
      //   if (this.title) {
      //     this.title.classList.remove('anchor-links__title--expanded-mobile');

      //     // Keep the divider line if there's a selected label, otherwise remove it
      //     if (!this.selectedLabel || !this.selectedLabel.textContent.trim()) {
      //       this.title.classList.remove('anchor-links__title--show-divider');
      //     }
      //   }

      //   // Don't modify window scroll position when closing dropdown
      // }, 300);
    }
  };

  scrollActiveIntoView() {
    // If we don't have an active link or the list is expanded, no need to scroll
    if (!this.activeLink || this.isExpanded) return;

    // Get the active link's parent (the list item) immediately
    const activeItem = this.activeLink.parentElement;
    if (!activeItem) return;

    // Force direct scroll positioning instead of using setTimeout
    // This ensures the active link is immediately visible in collapsed state

    // Use direct offsetTop calculation for more reliable positioning
    // Position the active item near the top of the visible area with padding
    const padding = 10;

    // Use direct scrollTop assignment for immediate effect without animation
    // This is more reliable when the UI is in transition
    this.list.scrollTop = activeItem.offsetTop - padding;

    // Also ensure any parent scrollable elements are updated
    activeItem.scrollIntoView({ block: 'nearest' });

    // Double-check visibility after a small delay and adjust if needed
    setTimeout(() => {
      const listRect = this.list.getBoundingClientRect();
      const activeRect = activeItem.getBoundingClientRect();

      // If still not visible after direct positioning, try again with different approach
      if (activeRect.top < listRect.top || activeRect.bottom > listRect.bottom) {
        // For items above visible area
        if (activeRect.top < listRect.top) {
          this.list.scrollTop = activeItem.offsetTop - padding;
        }
        // For items below visible area
        else if (activeRect.bottom > listRect.bottom) {
          this.list.scrollTop = (activeItem.offsetTop + activeRect.height) - listRect.height + padding;
        }
      }
    }, 50);
  };

  // onResize() {
  //   setCssVar(this.componentRoot, '--anchor-links-height', `${this.elem.offsetHeight}px`);
  // }

  /**
 * Sets up event listeners for device-specific navigation (mobile/desktop).
 */
  deviceEventController() {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

    if (isMobile) {
      this.deviceType = 'mobile';
      if (this.previousDeviceType === this.deviceType) return;
      document.removeEventListener('scroll', throttle(this.onScroll.bind(this), 50));
    }
    if (isDesktop) {
      this.deviceType = 'desktop';
      if (this.previousDeviceType === this.deviceType) return;
      this.onScroll();
      document.addEventListener('scroll', throttle(this.onScroll.bind(this), 50));
    }
    this.previousDeviceType = this.deviceType;
  }

  onScroll() {
    if (window.scrollY > this.componentRoot.offsetTop + 300) {
      // this.elem.classList.add('anchor-links--below-the-fold');
    } else {
      // this.elem.classList.remove('anchor-links--below-the-fold');
    }
  }
}
